import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { States } from "../../../__generated__/types";
import { AddressAutocomplete } from "../../../components/address-autocomplete";
import { useUserContextDispatch } from "../../../hooks/userContext";
import { PaymentTokenManager } from "../../../services/paymentApi";
import "./styles.scss";

interface Props {
  step: number;
  setStep: (value: number) => void;
  setNextStep: () => void;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  zipCode?: string;
  city?: string;
  state?: States;
  address?: string;
  onChange: (field: string, value: string | States) => void;
  isValid: boolean;
  userAccountId?: string;
  selectedPlan?: string;
}

export const HomeownerAddressFieldset = ({
  step,
  setStep,
  setNextStep,
  email,
  firstName,
  lastName,
  phoneNumber,
  zipCode,
  city,
  state,
  address,
  onChange,
  isValid,
  userAccountId,
  selectedPlan,
}: Props) => {
  const { updateHomeownerAccountData } = useUserContextDispatch();

  const handleCreateAccount = async () => {
    if (!isValid) return;

    // Only save to database if we have a valid user ID (not the placeholder)
    if (userAccountId && userAccountId !== 'resume-registration') {
      // Ensure we have a valid subscription - check payment data if selectedPlan is empty
      let finalSubscription = selectedPlan;
      if (!finalSubscription || finalSubscription === '') {
        const paymentData = PaymentTokenManager.getPaymentData();
        if (paymentData) {
          if (paymentData.planType === 'Guild' || paymentData.planName === 'Guild' || paymentData.productTier === 'guild') {
            finalSubscription = 'Guild';
          } else if (paymentData.planType === 'GuildPlus' || paymentData.planName === 'Guild+' || paymentData.productTier === 'guildplus') {
            finalSubscription = 'GuildPlus';
          } else {
            finalSubscription = 'basic';
          }
        } else {
          finalSubscription = 'basic';
        }
      }
      
      const userData = {
        _id: userAccountId,
        firstName: firstName || '',
        lastName: lastName || '',
        phoneNumber: phoneNumber || '',
        address: address || '',
        city: city || '',
        state: state || States.Al,
        zipCode: zipCode || '',
        subscription: finalSubscription,
      };


      try {
        // Save user data to database first
        await updateHomeownerAccountData(userData);
      } catch (error) {
        // Continue to payment modal even if save fails
      }
    } else {
    }
    
    // For Basic plan users, go directly to completion step (no payment required)
    if (selectedPlan === 'basic') {
      setNextStep(); // This will go to the completion step
    } else {
      // For paid plans, proceed to payment modal
      setNextStep();
    }
  };
  return (
    <Box className="homeowner-address-fieldset">
      <Typography variant="h5" className="form-title">
        Review & Confirm Your Information
      </Typography>

      <Box sx={{ 
        backgroundColor: '#F0FDF4', 
        border: '1px solid #86EFAC', 
        borderRadius: '8px', 
        padding: '12px 16px', 
        marginBottom: '24px' 
      }}>
        <Typography variant="body2" sx={{ color: '#166534', fontSize: '14px' }}>
          Your billing information has been pre-filled below. You can edit any field or use a different address for your account.
        </Typography>
      </Box>

      <Typography className="email-label">
        <b>Email Address:</b> {email}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name *"
            variant="outlined"
            fullWidth
            value={firstName || ""}
            onChange={(e) => onChange("firstName", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Last Name *"
            variant="outlined"
            fullWidth
            value={lastName || ""}
            onChange={(e) => onChange("lastName", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Phone Number *"
            variant="outlined"
            fullWidth
            value={phoneNumber || ""}
            onChange={(e) => onChange("phoneNumber", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <AddressAutocomplete
            label="Mailing Address *"
            variant="outlined"
            fullWidth
            value={address || ""}
            placeholder="Start typing your address..."
            onAddressSelect={(addressComponents) => {
              
              // Auto-fill address fields based on Google Places result
              if (addressComponents.formattedAddress) {
                // Set the main address field to street address
                const streetAddress = [
                  addressComponents.streetNumber,
                  addressComponents.route
                ].filter(Boolean).join(' ');
                onChange("address", streetAddress || addressComponents.formattedAddress);
              }
              
              // Auto-fill city
              if (addressComponents.locality) {
                onChange("city", addressComponents.locality);
              }
              
              // Auto-fill state
              if (addressComponents.administrativeAreaLevel1) {
                onChange("state", addressComponents.administrativeAreaLevel1 as States);
              }
              
              // Auto-fill zip code
              if (addressComponents.postalCode) {
                onChange("zipCode", addressComponents.postalCode);
              }
            }}
            onInputChange={(value) => onChange("address", value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Zipcode *"
            variant="outlined"
            fullWidth
            value={zipCode || ""}
            onChange={(e) => onChange("zipCode", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="City *"
            variant="outlined"
            fullWidth
            value={city || ""}
            onChange={(e) => onChange("city", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            label="State *"
            variant="outlined"
            fullWidth
            value={state || ""}
            onChange={(e) => onChange("state", e.target.value as States)}
          >
            {Object.values(States).map((st) => (
              <MenuItem key={st} value={st}>
                {st}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Box className="actions">
        <IconButton className="back-btn" onClick={() => setStep(step - 1)}>
          <ArrowBackIcon />
          <Typography>Back</Typography>
        </IconButton>

        <Button
          variant="contained"
          className="submit-btn"
          onClick={handleCreateAccount}
          disabled={!isValid}
        >
          {selectedPlan === 'basic' ? 'Create Free Account' : 'Create Account'}
        </Button>
      </Box>

      <Typography variant="caption" className="legal-text">
        By Signing up you agree to our{" "}
        <a href="/terms" target="_blank" rel="noreferrer">
          Terms
        </a>{" "}
        and{" "}
        <a href="/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        .
      </Typography>
    </Box>
  );
};
